import { coreServiceRequest, publicCoreServiceRequest } from '../utils/apiClient';
import { logError, logDebug } from './LoggingService';
import { findDefaultHead } from '../utils/headUtils';

/**
 * Core Service
 * Handles all API operations with the core backend service
 */

// ============================================================================
// PROJECT OPERATIONS
// ============================================================================

/**
 * Fetch all projects for the current user
 * @returns {Promise<Array>} Array of user projects
 * @throws When the request fails, so callers can tell "no projects" from "could not load"
 */
export const fetchUserProjects = async () => {
  try {
    // Use core service API for consistency with project creation
    const response = await coreServiceRequest('/api/projects');

    return response?.data?.projects || [];
  } catch (err) {
    logError('Error in fetchUserProjects', err, 'CoreService');
    throw err;
  }
};

/**
 * Create a new project with auto-generated API key
 * @param {string} name - Project name
 * @param {string} description - Project description (optional)
 * @param {string} repoUrl - GitHub repository URL (optional)
 * @returns {Promise<Object|null>} Created project or null if failed
 */
export const createProject = async (name, description = '', repoUrl = '') => {
  try {
    const requestBody = {
      name,
      description
    };

    // Only include repo_url if it's provided
    if (repoUrl && repoUrl.trim()) {
      requestBody.repo_url = repoUrl.trim();
    }

    // Use core service to create project (handles API key generation and hashing)
    const response = await coreServiceRequest('/api/projects', {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });

    logDebug('Core service response', response, 'CoreService');

    return response?.data?.project;
  } catch (err) {
    logError('Error in createProject', err, 'CoreService');
    throw err;
  }
};

/**
 * Delete a project and all its related data
 * @param {string} projectId - Project ID to delete
 * @returns {Promise<Object>} Deletion result with detailed information
 */
export const deleteProject = async (projectId) => {
  try {
    const response = await coreServiceRequest(`/api/projects/${projectId}`, {
      method: 'DELETE'
    });

    return { success: true, ...response?.data };
  } catch (err) {
    logError('Error in deleteProject', err, 'CoreService');
    throw err;
  }
};

/**
 * Update a project
 * @param {string} projectId - Project ID to update
 * @param {Object} updates - Update data
 * @returns {Promise<Object>} Updated project
 */
export const updateProject = async (projectId, updates) => {
  try {
    const response = await coreServiceRequest(`/api/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });

    return response?.data;
  } catch (err) {
    logError('Error in updateProject', err, 'CoreService');
    throw err;
  }
};

/**
 * Leave a project (remove self as member)
 * @param {string} projectId - Project ID to leave
 * @returns {Promise<Object>} Result of operation
 */
export const leaveProject = async (projectId) => {
  try {
    const response = await coreServiceRequest(`/api/projects/${projectId}/leave`, {
      method: 'POST'
    });

    return { success: true, ...response?.data };
  } catch (err) {
    logError('Error in leaveProject', err, 'CoreService');
    throw err;
  }
};

/**
 * Get a specific project
 * @param {string} projectId - Project ID
 * @returns {Promise<Object|null>} Project data, or null when the response carries none
 * @throws When the request fails (including 404 and network errors)
 */
export const getProject = async (projectId) => {
  try {
    const response = await coreServiceRequest(`/api/projects/${projectId}`);
    return response?.data?.project || null;
  } catch (err) {
    logError('Error in getProject', err, 'CoreService');
    throw err;
  }
};

/**
 * Regenerate API key for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} New API key data
 */
export const regenerateApiKey = async (projectId) => {
  try {
    const response = await coreServiceRequest(`/api/projects/${projectId}/regenerate-api-key`, {
      method: 'POST'
    });

    return response?.data;
  } catch (err) {
    logError('Error in regenerateApiKey', err, 'CoreService');
    throw err;
  }
};

// ============================================================================
// TARGETS OPERATIONS
// ============================================================================

/**
 * Fetch all targets for a specific project
 * @param {string} projectId - Project ID
 * @returns {Promise<Array>} Array of targets
 * @throws When the request fails, so callers can tell "no targets" from "could not load"
 */
export const fetchProjectTargets = async (projectId) => {
  try {
    const response = await coreServiceRequest(`/api/${projectId}/targets`);

    return response?.data?.targets || [];
  } catch (err) {
    logError('Error in fetchProjectTargets', err, 'CoreService');
    throw err;
  }
};

/**
 * Delete one or more targets and all their associated data
 * @param {string} projectId - Project ID
 * @param {string[]} targetNames - Array of target names to delete
 * @returns {Promise<Object>} Deletion result with deleted_count
 */
export const deleteTargets = async (projectId, targetNames) => {
  const BATCH_SIZE = 100;
  try {
    let totalDeleted = 0;
    for (let i = 0; i < targetNames.length; i += BATCH_SIZE) {
      const batch = targetNames.slice(i, i + BATCH_SIZE);
      const response = await coreServiceRequest(
        `/api/${projectId}/targets`,
        {
          method: 'DELETE',
          body: JSON.stringify({ target_names: batch }),
        }
      );
      totalDeleted += response?.data?.deleted_count || batch.length;
    }

    return { success: true, deleted_count: totalDeleted };
  } catch (err) {
    logError('Error in deleteTargets', err, 'CoreService');
    throw err;
  }
};

// ============================================================================
// TARGET REGION CONFIGURATION OPERATIONS
// ============================================================================

/**
 * Fetch region configuration for a target
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @returns {Promise<Object|null>} Region configuration or null if not found
 */
export const fetchTargetRegionConfig = async (projectId, targetName) => {
  try {
    const response = await coreServiceRequest(`/api/${projectId}/${encodeURIComponent(targetName)}/region-config`);
    return response?.data?.region_config || null;
  } catch (err) {
    logError('Error in fetchTargetRegionConfig', err, 'CoreService');
    return null;
  }
};

/**
 * Update region configuration for a target
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @param {Object} regionConfig - Region configuration object
 * @returns {Promise<Object>} Updated configuration
 */
export const updateTargetRegionConfig = async (projectId, targetName, regionConfig) => {
  try {
    const response = await coreServiceRequest(`/api/${projectId}/${encodeURIComponent(targetName)}/region-config`, {
      method: 'PUT',
      body: JSON.stringify({
        region_config: regionConfig
      })
    });

    return response?.data;
  } catch (err) {
    logError('Error in updateTargetRegionConfig', err, 'CoreService');
    throw err;
  }
};

/**
 * Discover available region names for a target
 * Fetches the latest commit's memory report to discover available regions
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @returns {Promise<Array<string>>} Array of region names or empty array
 */
export const discoverTargetRegions = async (projectId, targetName) => {
  try {
    // Get target heads to find the latest commit
    const heads = await fetchTargetHeads(projectId, targetName);
    if (!heads || heads.length === 0) {
      return [];
    }

    // Use the default head to get a sample report
    const latestCommitSha = findDefaultHead(heads).commit_sha;

    // Fetch memory report for this commit
    const report = await fetchMemoryReportByCommit(projectId, targetName, latestCommitSha);
    if (!report?.memory_analysis?.memory_layout) {
      return [];
    }

    // Extract region names from memory layout
    const regionNames = Object.keys(report.memory_analysis.memory_layout);
    return regionNames.sort();
  } catch (err) {
    logError('Error in discoverTargetRegions', err, 'CoreService');
    return [];
  }
};

// ============================================================================
// COMMITS OPERATIONS
// ============================================================================

/**
 * Fetch all commits for a project and target
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @param {number} limit - Number of commits to fetch (default: 50)
 * @param {number} offset - Number of commits to skip (default: 0)
 * @param {string} branchName - Specific branch name to fetch commits from (optional)
 * @returns {Promise<Array>} Array of commit objects with memory data
 */
/**
 * Fetch commits by branch commit SHA
 * @param {string} projectId - Project ID  
 * @param {string} targetName - Target name
 * @param {string} commitSha - Branch head commit SHA
 * @param {number} limit - Number of commits to fetch (default: 50)
 * @param {number} offset - Number of commits to skip (default: 0)
 * @returns {Promise<Object>} Commit data with pagination
 */
export const fetchCommitsByBranch = async (projectId, targetName, commitSha, limit = 50, offset = 0, onlyChanges = false) => {
  try {
    // Fetch commit chain starting from the branch head commit SHA
    const chainData = await fetchCommitChain(projectId, targetName, commitSha, limit, offset, onlyChanges);
    if (!chainData?.commits) {
      return {
        commits: [],
        pagination: { total_in_chain: 0, has_more: false },
        chain_summary: { total_commits: 0 }
      };
    }
    
    return chainData;
  } catch (err) {
    logError('Error in fetchCommitsByBranch', err, 'CoreService');
    return {
      commits: [],
      pagination: { total_in_chain: 0, has_more: false },
      chain_summary: { total_commits: 0 }
    };
  }
};

export const fetchProjectCommits = async (projectId, targetName = null, limit = 50, offset = 0) => {
  try {
    // If no target specified, get the first target for this project
    if (!targetName) {
      const targets = await fetchProjectTargets(projectId);
      if (!targets || targets.length === 0) {
        return [];
      }
      targetName = targets[0].name;
    }
    
    // Get target heads to find the latest commit SHA
    const heads = await fetchTargetHeads(projectId, targetName);
    if (!heads || heads.length === 0) {
      return [];
    }
    
    // Use the default head as starting point
    const startingCommitSha = findDefaultHead(heads).commit_sha;
    
    // Fetch commit chain starting from the latest commit
    const chainData = await fetchCommitChain(projectId, targetName, startingCommitSha, limit, offset);
    if (!chainData?.commits) {
      return {
        commits: [],
        pagination: { total_in_chain: 0, has_more: false },
        chain_summary: { total_commits: 0 }
      };
    }
    
    return chainData;
  } catch (err) {
    logError('Error in fetchProjectCommits', err, 'CoreService');
    return [];
  }
};

/**
 * Fetch total count of commits for a project and target
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @returns {Promise<number>} Total number of commits
 */
export const fetchProjectCommitsCount = async (projectId, targetName = null) => {
  try {
    if (!targetName) {
      const targets = await fetchProjectTargets(projectId);
      if (!targets || targets.length === 0) {
        return 0;
      }
      targetName = targets[0].name;
    }
    
    // Get target heads to find the latest commit SHA
    const heads = await fetchTargetHeads(projectId, targetName);
    if (!heads || heads.length === 0) {
      return 0;
    }
    
    // Use the default head as starting point
    const startingCommitSha = findDefaultHead(heads).commit_sha;
    
    // Use a single small request to get total count from pagination metadata
    const chainData = await fetchCommitChain(projectId, targetName, startingCommitSha, 1, 0);
    return chainData?.pagination?.total_in_chain || chainData?.commits?.length || 0;
  } catch (err) {
    logError('Error in fetchProjectCommitsCount', err, 'CoreService');
    return 0;
  }
};

// ============================================================================
// HELPER FUNCTIONS FOR NEW API INTEGRATION
// ============================================================================




// ============================================================================
// NEW API ENDPOINT FUNCTIONS
// ============================================================================

/**
 * Fetch memory report for a specific commit using new API
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @param {string} commitSha - Git commit SHA
 * @returns {Promise<Object|null>} Memory report or null if not found
 */
export const fetchMemoryReportByCommit = async (projectId, targetName, commitSha) => {
  try {
    const response = await coreServiceRequest(`/api/${projectId}/${encodeURIComponent(targetName)}/commit/${commitSha}?include_symbols=true`);
    return response?.data;
  } catch (err) {
    logError('Error in fetchMemoryReportByCommit', err, 'CoreService');
    return null;
  }
};

/**
 * Fetch memory diff between two commits
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @param {string} oldCommitSha - Old commit SHA
 * @param {string} newCommitSha - New commit SHA
 * @param {boolean} allSymbols - If true, include all symbols (changed+unchanged), if false include only changed symbols
 * @returns {Promise<Object|null>} Memory diff data or null if not found
 */
export const fetchMemoryDiffByCommits = async (projectId, targetName, oldCommitSha, newCommitSha, allSymbols = false) => {
  try {
    const response = await coreServiceRequest(`/api/${projectId}/${encodeURIComponent(targetName)}/commit/${oldCommitSha}/${newCommitSha}?all_symbols=${allSymbols}`);
    return response?.data;
  } catch (err) {
    logError('Error in fetchMemoryDiffByCommits', err, 'CoreService');
    // Return error object instead of re-throwing to avoid uncaught promise errors
    return { success: false, error: err.message };
  }
};

/**
 * Fetch commit chain starting from given SHA using new API
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @param {string} commitSha - Starting commit SHA
 * @param {number} count - Number of commits to fetch (default: 50)
 * @param {number} offset - Number of commits to skip (default: 0)
 * @returns {Promise<Object|null>} Commit chain data or null if not found
 */
export const fetchCommitChain = async (projectId, targetName, commitSha, count = 50, offset = 0, onlyChanges = false) => {
  try {
    const params = new URLSearchParams({
      count: count.toString(),
      offset: offset.toString()
    });
    
    if (onlyChanges) {
      params.append('only_changed', 'true');
    }
    
    const response = await coreServiceRequest(`/api/${projectId}/${encodeURIComponent(targetName)}/commits/${commitSha}?${params}`);
    return response?.data;
  } catch (err) {
    logError('Error in fetchCommitChain', err, 'CoreService');
    return null;
  }
};


/**
 * Fetch heads (latest commits for branches/PRs/tags) for a target using new API
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @returns {Promise<Array>} Array of head records or empty array
 * @throws When the request fails, so callers can tell "no heads" from "could not load"
 */
export const fetchTargetHeads = async (projectId, targetName) => {
  try {
    const response = await coreServiceRequest(`/api/${projectId}/${encodeURIComponent(targetName)}/heads`);
    return response?.data?.heads || [];
  } catch (err) {
    logError('Error in fetchTargetHeads', err, 'CoreService');
    throw err;
  }
};


// ============================================================================
// MEMORY DIFF AND COMPARISON OPERATIONS
// ============================================================================


/**
 * Fetch storage efficiency statistics
 * @param {string} projectId - Project ID
 * @returns {Promise<Object|null>} Storage stats or null if not found
 */
export const fetchStorageStats = async (projectId) => {
  try {
    const params = new URLSearchParams({
      project_id: projectId
    });

    const data = await coreServiceRequest(`/api/memory/storage-stats?${params}`);
    return data;
  } catch (err) {
    logError('Error in fetchStorageStats', err, 'CoreService');
    return null;
  }
};

// ============================================================================
// BUDGET OPERATIONS
// ============================================================================

/**
 * Fetch budgets for a target with calculated usage for specific commit
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @param {string} commitSha - Commit SHA for budget calculations
 * @returns {Promise<Array>} Array of budgets with usage data
 */
export const fetchBudgets = async (projectId, targetName, commitSha) => {
  try {
    const response = await coreServiceRequest(`/api/${projectId}/${encodeURIComponent(targetName)}/budgets/commit/${commitSha}`);
    return response?.data?.budgets || [];
  } catch (err) {
    logError('Error in fetchBudgets', err, 'CoreService');
    throw err;
  }
};

/**
 * Create a new budget for a target
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @param {Object} budget - Budget object with name, keywords, and regions
 * @returns {Promise<Object>} Created budget
 */
export const createBudget = async (projectId, targetName, budget) => {
  try {
    const response = await coreServiceRequest(`/api/${projectId}/${encodeURIComponent(targetName)}/budgets`, {
      method: 'POST',
      body: JSON.stringify(budget)
    });

    return response?.data;
  } catch (err) {
    logError('Error in createBudget', err, 'CoreService');
    throw err;
  }
};

/**
 * Update an existing budget
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @param {string} budgetId - Budget ID
 * @param {Object} updates - Budget updates
 * @returns {Promise<Object>} Updated budget
 */
export const updateBudget = async (projectId, targetName, budgetId, updates) => {
  try {
    const response = await coreServiceRequest(`/api/${projectId}/${encodeURIComponent(targetName)}/budgets/${budgetId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });

    return response?.data;
  } catch (err) {
    logError('Error in updateBudget', err, 'CoreService');
    throw err;
  }
};

/**
 * Delete a budget
 * @param {string} projectId - Project ID
 * @param {string} targetName - Target name
 * @param {string} budgetId - Budget ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteBudget = async (projectId, targetName, budgetId) => {
  try {
    const response = await coreServiceRequest(`/api/${projectId}/${encodeURIComponent(targetName)}/budgets/${budgetId}`, {
      method: 'DELETE'
    });

    return { success: true, ...response?.data };
  } catch (err) {
    logError('Error in deleteBudget', err, 'CoreService');
    throw err;
  }
};

// ============================================================================
// PROJECT SHARING - INVITATIONS AND MEMBERS
// ============================================================================

/**
 * Create a project invitation
 * @param {string} projectId - Project ID
 * @param {string} email - Invitee email address
 * @param {string} role - Role to assign ('viewer' or 'admin'; only the owner may invite admins)
 * @returns {Promise<Object>} Invitation data
 */
export const createInvitation = async (projectId, email, role = 'viewer') => {
  try {
    const response = await coreServiceRequest(`/api/projects/${projectId}/invitations`, {
      method: 'POST',
      body: JSON.stringify({ email, role })
    });

    return response?.data;
  } catch (err) {
    logError('Error in createInvitation', err, 'CoreService');
    throw err;
  }
};

/**
 * List all invitations for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Array>} Array of invitations
 */
export const listInvitations = async (projectId) => {
  try {
    const response = await coreServiceRequest(`/api/projects/${projectId}/invitations`);

    return response?.data?.invitations || [];
  } catch (err) {
    logError('Error in listInvitations', err, 'CoreService');
    return [];
  }
};

/**
 * Cancel a pending invitation
 * @param {string} projectId - Project ID
 * @param {string} invitationId - Invitation ID
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelInvitation = async (projectId, invitationId) => {
  try {
    const response = await coreServiceRequest(`/api/projects/${projectId}/invitations/${invitationId}`, {
      method: 'DELETE'
    });

    return { success: true, ...response?.data };
  } catch (err) {
    logError('Error in cancelInvitation', err, 'CoreService');
    throw err;
  }
};

/**
 * Get invitation details by token (public endpoint - no authentication required)
 * @param {string} token - Invitation token
 * @returns {Promise<Object|null>} Invitation details or null if not found
 */
export const getInvitationByToken = async (token) => {
  try {
    const response = await publicCoreServiceRequest(`/api/invitations/${token}`);

    return response?.data?.invitation || null;
  } catch (err) {
    logError('Error in getInvitationByToken', err, 'CoreService');
    return null;
  }
};

/**
 * Get all pending invitations for the authenticated user
 * @returns {Promise<Array>} Array of pending invitations
 */
export const getUserPendingInvitations = async () => {
  try {
    const response = await coreServiceRequest('/api/invitations/me');

    return response?.data?.invitations || [];
  } catch (err) {
    logError('Error in getUserPendingInvitations', err, 'CoreService');
    return [];
  }
};

/**
 * Accept a project invitation
 * @param {string} token - Invitation token
 * @returns {Promise<Object>} Acceptance result with project_id and role
 */
export const acceptInvitation = async (token) => {
  try {
    const response = await coreServiceRequest(`/api/invitations/${token}/accept`, {
      method: 'POST'
    });

    return response?.data;
  } catch (err) {
    logError('Error in acceptInvitation', err, 'CoreService');
    throw err;
  }
};

/**
 * Decline a project invitation
 * @param {string} token - Invitation token
 * @returns {Promise<Object>} Decline result
 */
export const declineInvitation = async (token) => {
  try {
    const response = await coreServiceRequest(`/api/invitations/${token}/decline`, {
      method: 'POST'
    });

    return response?.data;
  } catch (err) {
    logError('Error in declineInvitation', err, 'CoreService');
    throw err;
  }
};

/**
 * List all members of a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} Object containing members array and current_user_role
 */
export const listMembers = async (projectId) => {
  try {
    const response = await coreServiceRequest(`/api/projects/${projectId}/members`);

    return response?.data || { members: [], current_user_role: null };
  } catch (err) {
    logError('Error in listMembers', err, 'CoreService');
    return { members: [], current_user_role: null };
  }
};

/**
 * Remove a member from a project
 * @param {string} projectId - Project ID
 * @param {string} memberId - Member ID
 * @returns {Promise<Object>} Removal result
 */
export const removeMember = async (projectId, memberId) => {
  try {
    const response = await coreServiceRequest(`/api/projects/${projectId}/members/${memberId}`, {
      method: 'DELETE'
    });

    return { success: true, ...response?.data };
  } catch (err) {
    logError('Error in removeMember', err, 'CoreService');
    throw err;
  }
};

/**
 * Change a member's role (owner only)
 * @param {string} projectId - Project ID
 * @param {string} memberId - Member ID
 * @param {string} role - New role ('viewer' or 'admin')
 * @returns {Promise<Object>} Update result
 */
export const updateMemberRole = async (projectId, memberId, role) => {
  try {
    const response = await coreServiceRequest(`/api/projects/${projectId}/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify({ role })
    });

    return response?.data;
  } catch (err) {
    logError('Error in updateMemberRole', err, 'CoreService');
    throw err;
  }
};

/**
 * Get current user's role in a project
 * @param {string} projectId - Project ID
 * @returns {Promise<string|null>} User role ('owner', 'admin', 'viewer', or null)
 */
export const getUserProjectRole = async (projectId) => {
  try {
    const response = await listMembers(projectId);
    return response?.current_user_role || null;
  } catch (err) {
    logError('Error in getUserProjectRole', err, 'CoreService');
    return null;
  }
};

// ============================================================================
// DEMO CONTACT OPERATIONS
// ============================================================================

/**
 * Submit demo contact form
 * @param {Object} formData - Contact form data
 * @returns {Promise<Object>} Response from submission
 */
export const submitDemoContactForm = async (formData) => {
  try {
    const response = await publicCoreServiceRequest('/api/demo/contact', {
      method: 'POST',
      body: JSON.stringify(formData)
    });

    return response?.data;
  } catch (err) {
    logError('Error in submitDemoContactForm', err, 'CoreService');
    throw err;
  }
};

// ============================================================================
// PUBLIC PROJECT ACCESS OPERATIONS
// ============================================================================

/**
 * Fetch public project info by GitHub path
 * @param {string} githubAuthor - GitHub repository author
 * @param {string} githubProject - GitHub repository project name
 * @returns {Promise<Object>} Public project info
 */
export const fetchPublicProject = async (githubAuthor, githubProject) => {
  try {
    const response = await publicCoreServiceRequest(
      `/api/public/${githubAuthor}/${githubProject}/info`
    );
    return response?.data?.project;
  } catch (err) {
    logError('Error in fetchPublicProject', err, 'CoreService');
    throw err;
  }
};

/**
 * Fetch targets for public project
 * @param {string} githubAuthor - GitHub repository author
 * @param {string} githubProject - GitHub repository project name
 * @returns {Promise<Array>} Targets array
 */
export const fetchPublicTargets = async (githubAuthor, githubProject) => {
  try {
    const response = await publicCoreServiceRequest(
      `/api/public/${githubAuthor}/${githubProject}/targets`
    );
    return response?.data?.targets || [];
  } catch (err) {
    logError('Error in fetchPublicTargets', err, 'CoreService');
    throw err;
  }
};

/**
 * Fetch targets with memory overview for public project (optimized single call)
 * Returns all targets with their default branch head and memory_layout in one request.
 * @param {string} githubAuthor - GitHub repository author
 * @param {string} githubProject - GitHub repository project name
 * @returns {Promise<Object>} Object with targets array and default_branch
 */
export const fetchPublicTargetsOverview = async (githubAuthor, githubProject, commitSha = null) => {
  try {
    let url = `/api/public/${githubAuthor}/${githubProject}/targets-overview`;
    if (commitSha) {
      url += `?commit_sha=${encodeURIComponent(commitSha)}`;
    }
    const response = await publicCoreServiceRequest(url);
    return {
      targets: response?.data?.targets || [],
      defaultBranch: response?.data?.default_branch || null
    };
  } catch (err) {
    logError('Error in fetchPublicTargetsOverview', err, 'CoreService');
    throw err;
  }
};

/**
 * Fetch targets with memory overview for authenticated project (optimized single call)
 * Returns all targets with their default branch head and memory_layout in one request.
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} Object with targets array and default_branch
 */
export const fetchTargetsOverview = async (projectId, commitSha = null) => {
  try {
    let url = `/api/${projectId}/targets-overview`;
    if (commitSha) {
      url += `?commit_sha=${encodeURIComponent(commitSha)}`;
    }
    const response = await coreServiceRequest(url);
    return {
      targets: response?.data?.targets || [],
      defaultBranch: response?.data?.default_branch || null
    };
  } catch (err) {
    logError('Error in fetchTargetsOverview', err, 'CoreService');
    throw err;
  }
};

/**
 * Fetch heads for public project target
 * @param {string} githubAuthor - GitHub repository author
 * @param {string} githubProject - GitHub repository project name
 * @param {string} targetName - Target name
 * @returns {Promise<Array>} Heads array
 */
export const fetchPublicHeads = async (githubAuthor, githubProject, targetName) => {
  try {
    const response = await publicCoreServiceRequest(
      `/api/public/${githubAuthor}/${githubProject}/target/${encodeURIComponent(targetName)}/heads`
    );
    return response?.data?.heads || [];
  } catch (err) {
    logError('Error in fetchPublicHeads', err, 'CoreService');
    throw err;
  }
};

/**
 * Fetch commits for public project
 * @param {string} githubAuthor - GitHub repository author
 * @param {string} githubProject - GitHub repository project name
 * @param {string} targetName - Target name
 * @param {string} commitSha - Commit SHA to start from
 * @param {number} count - Number of commits to fetch
 * @param {number} offset - Offset for pagination
 * @param {boolean} onlyChanged - Only return commits with changes
 * @returns {Promise<Object>} Object with commits array and total_count
 */
export const fetchPublicCommits = async (
  githubAuthor,
  githubProject,
  targetName,
  commitSha,
  count = 50,
  offset = 0,
  onlyChanged = true
) => {
  try {
    const params = new URLSearchParams({
      count: count.toString(),
      offset: offset.toString(),
      only_changed: onlyChanged.toString()
    });

    const response = await publicCoreServiceRequest(
      `/api/public/${githubAuthor}/${githubProject}/target/${encodeURIComponent(targetName)}/commits/${commitSha}?${params}`
    );

    return {
      commits: response?.data?.commits || [],
      pagination: response?.data?.pagination || { total_in_chain: 0, has_more: false }
    };
  } catch (err) {
    logError('Error in fetchPublicCommits', err, 'CoreService');
    throw err;
  }
};

/**
 * Fetch memory report for public project commit
 * @param {string} githubAuthor - GitHub repository author
 * @param {string} githubProject - GitHub repository project name
 * @param {string} targetName - Target name
 * @param {string} commitSha - Commit SHA
 * @param {boolean} includeSymbols - Include symbols in report
 * @returns {Promise<Object>} Memory report
 */
export const fetchPublicMemoryReport = async (
  githubAuthor,
  githubProject,
  targetName,
  commitSha,
  includeSymbols = true
) => {
  try {
    const params = new URLSearchParams({
      include_symbols: includeSymbols.toString()
    });

    const response = await publicCoreServiceRequest(
      `/api/public/${githubAuthor}/${githubProject}/target/${encodeURIComponent(targetName)}/commit/${commitSha}?${params}`
    );

    return response?.data?.report;
  } catch (err) {
    logError('Error in fetchPublicMemoryReport', err, 'CoreService');
    throw err;
  }
};

/**
 * Fetch memory comparison between two commits for public project
 * @param {string} githubAuthor - GitHub author/organization name
 * @param {string} githubProject - GitHub project name
 * @param {string} targetName - Target name
 * @param {string} oldCommitSha - Old commit SHA (base)
 * @param {string} newCommitSha - New commit SHA (head)
 * @param {boolean} allSymbols - Include all symbols or only changed
 * @returns {Promise<Object>} Memory comparison data
 */
export const fetchPublicMemoryDiff = async (
  githubAuthor,
  githubProject,
  targetName,
  oldCommitSha,
  newCommitSha,
  allSymbols = false
) => {
  try {
    const params = new URLSearchParams({
      all_symbols: allSymbols.toString()
    });

    const response = await publicCoreServiceRequest(
      `/api/public/${githubAuthor}/${githubProject}/target/${encodeURIComponent(targetName)}/compare/${oldCommitSha}/${newCommitSha}?${params}`
    );

    return response?.data;
  } catch (err) {
    logError('Error in fetchPublicMemoryDiff', err, 'CoreService');
    throw err;
  }
};

/**
 * Fetch region configuration for a public project target
 * @param {string} githubAuthor - GitHub author/org name
 * @param {string} githubProject - GitHub project/repo name
 * @param {string} targetName - Target name
 * @returns {Promise<Object>} Region configuration
 */
export const fetchPublicRegionConfig = async (githubAuthor, githubProject, targetName) => {
  try {
    const response = await publicCoreServiceRequest(
      `/api/public/${githubAuthor}/${githubProject}/target/${encodeURIComponent(targetName)}/region-config`
    );
    return response?.data?.region_config;
  } catch (err) {
    logError('Error in fetchPublicRegionConfig', err, 'CoreService');
    throw err;
  }
};

/**
 * Fetch budgets for a public project target
 * @param {string} githubAuthor - GitHub author/org name
 * @param {string} githubProject - GitHub project/repo name
 * @param {string} targetName - Target name
 * @param {string} commitSha - Commit SHA for budget calculations
 * @returns {Promise<Array>} Array of budgets with usage data
 */
export const fetchPublicBudgets = async (githubAuthor, githubProject, targetName, commitSha) => {
  try {
    const response = await publicCoreServiceRequest(
      `/api/public/${githubAuthor}/${githubProject}/target/${encodeURIComponent(targetName)}/budgets/commit/${commitSha}`
    );
    return response?.data?.budgets || [];
  } catch (err) {
    logError('Error in fetchPublicBudgets', err, 'CoreService');
    throw err;
  }
};

/**
 * Update project public access setting
 * @param {string} projectId - Project ID
 * @param {boolean} enablePublicAccess - Enable or disable public access
 * @returns {Promise<Object>} Response with public URL if enabled
 */
export const updateProjectPublicAccess = async (projectId, enablePublicAccess) => {
  try {
    const response = await coreServiceRequest(
      `/api/projects/${projectId}/public-access`,
      {
        method: 'PUT',
        body: JSON.stringify({ enable_public_access: enablePublicAccess })
      }
    );
    return response?.data;
  } catch (err) {
    logError('Error in updateProjectPublicAccess', err, 'CoreService');
    throw err;
  }
};

// ============================================================================
// ADMIN OPERATIONS
// ============================================================================

export const checkAdminStatus = async () => {
  try {
    const response = await coreServiceRequest('/api/admin/dashboard/check');
    return response?.is_admin || false;
  } catch (err) {
    logDebug('Admin check failed (expected for non-admins)', err, 'CoreService');
    return false;
  }
};

export const fetchAdminOverview = async () => {
  try {
    const response = await coreServiceRequest('/api/admin/dashboard/overview');
    return response || {};
  } catch (err) {
    logError('Error in fetchAdminOverview', err, 'CoreService');
    throw err;
  }
};

export const fetchAdminProjects = async () => {
  try {
    const response = await coreServiceRequest('/api/admin/dashboard/projects');
    return response?.projects || [];
  } catch (err) {
    logError('Error in fetchAdminProjects', err, 'CoreService');
    throw err;
  }
};

